import withReactiveFields from "./reactivefields";

import {
  noop,
  callback,
  constant,
  identity,
  objectWithAccessors,
  withProperties,
  isPlainObject,
  isString,
  isFunction,
  resolveBoolean,
  isBoolean
} from "../../../utils";

const nullFunction = Object.freeze(constant(null));
const hasProperty = Function.prototype.call.bind(Object.prototype.hasOwnProperty);

const PLAIN_INPUT = 'PLAIN';
// const MASKED_INPUT = 'MASKED';
const SWITCH_INPUT = 'SWITCH';
const SELECTION_INPUT = 'SELECTION';

function __booleanFunction__(fn, defaultBool = true) {
  defaultBool = isBoolean(defaultBool) ? defaultBool : true;
  fn = callback(fn);

  return function __booleanFn__() {
    return resolveBoolean(fn(), defaultBool);
  }
}

function __createBaseInputObject__(props) {
  const inputObject = Object.create(null);
  let { available, persists, required } = isPlainObject(props) ? props : {};

  [ available, persists, required ] =
    [ available, persists, required ]
      .map(prop => (
        isFunction(prop)
          ? __booleanFunction__(prop)
          : constant(resolveBoolean(prop, true))
      ));

  return objectWithAccessors.call(inputObject, { available, persists, required });
}

function __createPlainInput__(props) {
  let { key: label, parser, propagate } = (props = isPlainObject(props) ? props : {});

  label = label && isString(label) ? label : null;
  parser = callback(parser, identity);
  propagate = callback(propagate, noop);

  if (label) {
    const inputObject = __createBaseInputObject__(props);
    let currentValue = __getDefaultValue__();

    objectWithAccessors.call(inputObject, {
      type: () => PLAIN_INPUT,
      defaultValue: __getDefaultValue__,

      value: {
        getter() { return currentValue },
        setter(value) {
          let current = currentValue;

          try {
            if (
              (value === null && current !== value) ||
              current !== (value = parser(value))
            ) { current = value == undefined ? null : value }

            currentValue !== current && propagate(currentValue = current);
          } catch (e) { (currentValue = null, propagate(e)) }
        }
      }
    }, false);

    return withProperties(Object.create(null), {
      [label]: Object.freeze(inputObject)
    });
  }

  throw new Error('Cannot setup input field without appropriate label.');

  function __getDefaultValue__() {
    try {
      return parser(props.defaultValue);
    } catch (e) { return null }
  }
}

function __createSwitchInput__(props) {
  let { key: label, propagate } = (props = isPlainObject(props) ? props : {});

  label = label && isString(label) ? label : null;
  propagate = callback(propagate, noop);

  if (label) {
    const inputObject = __createBaseInputObject__(props);
    let currentState = __getDefaultState__();

    objectWithAccessors.call(inputObject, {
      type: () => SWITCH_INPUT,
      defaultValue: __getDefaultState__,

      value: {
        getter() { return currentState },
        setter(value) {
          let state = currentState;
          state = value == undefined ? this.defaultValue : !state;
          currentState !== state && propagate(currentState = state);
        }
      }
    });

    return withProperties(Object.create(null), {
      [label]: Object.freeze(inputObject)
    });
  }

  throw new Error('Cannot setup switch input field without appropriate label.');

  function __getDefaultState__() {
    try {
      return resolveBoolean(props.defaultValue, false);
    } catch (e) { return false }
  }
}

function __createSelectionInput__(props) {
  let optionsList, cachedOptionsList, {
    key: label,
    propagate
  } = (props = isPlainObject(props) ? props : {});

  label = label && isString(label) ? label : null;
  propagate = callback(propagate, noop);

  if (label) {
    const inputObject = __createBaseInputObject__(props);
    let selected = __getDefaultSelection__();

    objectWithAccessors.call(inputObject, {
      type: () => SELECTION_INPUT,
      options: __getSelectionList__,
      defaultValue: __getDefaultSelection__,

      value: {
        getter() { return selected },
        setter(value) {
          let currentSelected = selected;

          try {
            if (
              (value === null && currentSelected !== value) ||
              currentSelected !== (value = __getSelection__(value))
            ) { currentSelected = value }

            selected !== currentSelected && propagate(selected = currentSelected);
          } catch (e) { (selected = null, propagate(e)) }
        }
      }
    });

    return withProperties(Object.create(null), {
      [label]: Object.freeze(inputObject)
    });
  }

  throw new Error('Cannot setup selection input field without appropriate label.');

  function __getSelectionList__() {
    return optionsList && optionsList === props.options
      ? cachedOptionsList
      : (cachedOptionsList = [].concat(optionsList = props.options).filter(Boolean));
  }

  function __getSelection__(selected) {
    const list = __getSelectionList__();
    const selector = callback(props.selector, nullFunction);

    if (list.indexOf(selected) >= 0) return selected;
    if (list.indexOf(selected = selector(selected)) >= 0) return selected;

    return null;
  }

  function __getDefaultSelection__() {
    const list = __getSelectionList__();

    return list.length === 1
      ? list[0]
      : __getSelection__(props.defaultValue);
  }
}

function withInputFields($context, fields) {
  if (!(isPlainObject($context) && Object.isExtensible($context))) {
    throw new Error('Cannot setup input fields without appropriate context object.');
  }

  const $inputs = Object.create(null);
  const __inputValuesPropagateMap__ = Object.create(null);

  const $getUpdatedInput = function __getInputUpdate__(key, valueOrError) {
    let [ value, error = null ] = [ valueOrError ];

    if (valueOrError instanceof Error) {
      [ value, error ] = [ error, value ];
    }

    const { value: _value, defaultValue, available, persists, ...props } = inputFields[key];
    const updatedInput = withProperties(Object.create(null), { value, ...props });

    error && withProperties(updatedInput, { error });

    return updatedInput;
  }

  const inputFields = isPlainObject(fields)
    ? Object.keys(fields).reduce((inputsObject, key) => {
      let props = fields[key];

      props = isPlainObject(props) ? props : ({
        defaultValue: isFunction(props) ? props.call($context) : props
      });

      if (isString(key) && Object.isExtensible(props)) {
        props['key'] = key;

        props['propagate'] = __inputValuesPropagateMap__[key] = (function __propagateFactory__() {
          const getUpdatedInput = $getUpdatedInput.bind(null, key);
          let timer, current;

          return function __propagate__(valueOrError) {
            timer && clearTimeout(timer);
            current = getUpdatedInput(valueOrError);

            timer = setTimeout(() => {
              $inputs[key] = current;
              timer && clearTimeout(timer);
              timer = undefined;
            }, 0);
          }
        })();

        inputsObject = {
          ...inputsObject,
          ...((
            hasProperty(props, 'options')
              ? __createSelectionInput__
              : isBoolean(props.defaultValue) ? __createSwitchInput__ : __createPlainInput__
          )(props))
        };
      }

      return inputsObject;
    }, Object.create(null))
    : Object.create(null);

  withReactiveFields($inputs, ...Object.keys(inputFields));

  Object.keys(__inputValuesPropagateMap__).forEach(key => {
    callback(__inputValuesPropagateMap__[key])(inputFields[key].defaultValue);
  });

  Object.defineProperties($inputs, {
    $available: {
      value(field) {
        return (
          hasProperty(inputFields, field) &&
          inputFields[field].available !== false
        );
      }
    },

    $refresh: {
      value(...fields) {
        (fields.length ? fields : Object.keys(inputFields))
          .forEach(key => {
            if (hasProperty(inputFields, key)) {
              const input = inputFields[key];
              input.type !== SWITCH_INPUT && (input.value = input.value);
              callback(__inputValuesPropagateMap__[key])(input.value);
            }
          });
      }
    },

    $reset: {
      value(...fields) {
        (fields.length ? fields : Object.keys(inputFields))
          .forEach(key => {
            if (hasProperty(inputFields, key)) {
              const input = inputFields[key];
              const defaultValue = input.defaultValue;
              input.value !== defaultValue && (input.value = defaultValue);
            }
          });
      }
    },

    $update: {
      value(key, value) {
        if (hasProperty(inputFields, key)) {
          inputFields[key].value = value;
        }
      }
    }
  });

  return withProperties($context, { $inputs });
}

export default withInputFields;
